<script lang="ts">
  import type { RestrictedOrganization } from "$lib/api/contract";
  import { session } from "$lib/stores/session.svelte";
  import { track } from "$lib/telemetry/emit";
  import { SUPPORT_EMAIL } from "$lib/domain/access";
  import { router } from "../../router.svelte";
  import AuthLayout from "../auth/AuthLayout.svelte";
  import Banner from "$lib/components/Banner.svelte";
  import Button from "$lib/components/Button.svelte";

  interface Props {
    organization: RestrictedOrganization;
  }
  const { organization }: Props = $props();

  // Deliberately minimal. The session carries no data for a network-restricted
  // organization — no licence, no members, no policy — so there is nothing to render
  // and nothing to retry in place. The only honest content is why, and how to recover.
  let announced = $state<string | null>(null);
  $effect(() => {
    if (announced !== organization.id) {
      announced = organization.id;
      track({ name: "organization.unlicensed_access", outcome: "network_restricted" });
    }
  });

  const otherOrganizations = $derived(session.organizations);

  async function signOut(): Promise<void> {
    await session.logout();
    router.navigate("/signed-out", { replace: true });
  }
</script>

<AuthLayout
  title="This network can't reach {organization.name}"
  subtitle="Your membership is fine — the connection you're on isn't allowed."
>
  <div class="stack">
    <Banner tone="warn" title="Blocked by an organization network policy">
      An administrator of {organization.name} restricted access to specific networks, and the
      address you're connecting from isn't one of them.
    </Banner>

    <p class="copy">
      Reconnect from an allowed network — usually your office network or your company VPN — and
      open this page again.
    </p>

    <p class="copy">
      If you believe the restriction is wrong, or nobody can reach the organization any more,
      email <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>. EpiDev support can disable
      the restriction for {organization.name}; the configured ranges are preserved.
    </p>

    {#if otherOrganizations.length > 0}
      <div class="others">
        <p class="others-title">Organizations you can reach from here</p>
        <ul>
          {#each otherOrganizations as org (org.id)}
            <li>
              <Button size="sm" variant="ghost" href={`/orgs/${org.id}/overview`}>{org.name}</Button>
            </li>
          {/each}
        </ul>
      </div>
    {/if}

    <Button variant="ghost" fullWidth onclick={signOut}>Sign out</Button>
  </div>
</AuthLayout>

<style>
  .copy {
    font-size: 14px;
    margin: 0;
  }
  .copy a {
    color: var(--accent);
  }
  .others {
    border-top: 1px solid var(--line-soft);
    padding-top: 14px;
  }
  .others-title {
    margin: 0 0 8px;
    font-size: 12.5px;
    color: var(--muted);
  }
  .others ul {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
</style>
